// ---------------------------------------------------------------------------
// Search analytics exclusion (GB #1392)
//
// OWNER: Bill. Update HOUSE_STORE_UIDS when house stores are added/sold.
// Staff domains: gearfocus.com, refocusmarket.com.
// Alternative if this list drifts: key off a house_store flag on the
// Firestore user doc (requires threading the user doc into search paths).
//
// When the signed-in user is GearFocus staff or a house store, Algolia
// search requests should carry `analytics: false` so their searches never
// pollute Algolia's search analytics. Search RESULTS are unchanged — this
// only stops analytics counting.
// ---------------------------------------------------------------------------

import { AUTH } from './firebase';

// Staff/internal email domains whose searches should never count toward
// Algolia search analytics.
const STAFF_EMAIL_DOMAINS = ['@gearfocus.com', '@refocusmarket.com'];

// House-store accounts (staff-operated storefronts) whose searches should
// also be excluded from Algolia search analytics.
const HOUSE_STORE_UIDS = [
  'lGXm9tkerwOfJcjBYTFvzRvFqlZ2', // The Refocus Store
  'URf4fzRqpWCmFIdPFcB6', // NeverStop Films Gear Shop
  '2fLsc9iqrRg3MSTOa5z0701ftaq2', // Fox King Media
  'UqnZVr7XklRb7d2Z9WrLj2eI6DE3', // Lacy's ATX Studio
];

// Pure decision function: is this user's search exempt from Algolia
// analytics? Exported for unit testing.
export function isAnalyticsExcluded(
  email: string | null | undefined,
  uid: string | null | undefined
): boolean {
  if (email) {
    const lowerEmail = email.toLowerCase();
    if (STAFF_EMAIL_DOMAINS.some((domain) => lowerEmail.endsWith(domain))) {
      return true;
    }
  }
  if (uid && HOUSE_STORE_UIDS.includes(uid)) {
    return true;
  }
  return false;
}

// Determine whether the CURRENT signed-in user's searches should be
// excluded from Algolia analytics. Reads the Firebase Auth instance
// synchronously at call time (not via React context, since callers are
// plain modules). MUST fail open (analytics stays ON) on any error or
// when running server-side, since search analytics is core product data
// (GB #1389) and a bug here must never silently disable it for everyone.
export function shouldExcludeCurrentUserFromAnalytics(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const currentUser = AUTH?.currentUser;
    if (!currentUser) return false;
    return isAnalyticsExcluded(currentUser.email, currentUser.uid);
  } catch {
    return false;
  }
}
